import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { api, type Paginated } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

export function AuditLogPage() {
  const { t, locale, localized } = useI18n();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [inspect, setInspect] = useState<any>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', page, filters],
    queryFn: () => api.get<Paginated<any>>('/audit-logs', { page, pageSize: 50, ...filters }),
  });

  const { data: facets } = useQuery({
    queryKey: ['audit-logs', 'facets'],
    queryFn: () => api.get<{ entityTypes: string[]; actions: string[] }>('/audit-logs/facets'),
  });

  return (
    <>
      {/* §27.2 — every action that can affect money or accountability leaves a row. */}
      <PageHeader title={t('audit.title')} subtitle={t('audit.sysSubtitle')} />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-4">
          <Select
            value={filters.entityType ?? ''}
            onChange={(e) => { setFilters({ ...filters, entityType: e.target.value }); setPage(1); }}
          >
            <option value="">{t('audit.entity')} — {t('common.all')}</option>
            {facets?.entityTypes.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </Select>
          <Select
            value={filters.action ?? ''}
            onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(1); }}
          >
            <option value="">{t('audit.action')} — {t('common.all')}</option>
            {facets?.actions.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </Select>
          <Input
            type="date"
            value={filters.from ?? ''}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          />
          <Input
            type="date"
            value={filters.to ?? ''}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          />
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<ScrollText className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('audit.occurredAt')}</Th>
                  <Th>{t('audit.actor')}</Th>
                  <Th>{t('audit.action')}</Th>
                  <Th>{t('audit.entity')}</Th>
                  <Th>{t('audit.app')}</Th>
                  <Th align="end">IP</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((log) => (
                  <Tr key={log.id} onClick={() => setInspect(log)}>
                    <Td className="whitespace-nowrap tabular text-muted">
                      {formatDateTime(log.occurredAt, locale)}
                    </Td>
                    <Td>{log.actor ? localized(log.actor) : <span className="text-faint">system</span>}</Td>
                    <Td>
                      <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{log.action}</span>
                    </Td>
                    <Td>
                      <Badge tone="neutral">{log.entityType}</Badge>
                      {log.entityId && (
                        <span className="ms-2 font-mono text-caption text-faint">
                          {String(log.entityId).slice(0, 8)}
                        </span>
                      )}
                    </Td>
                    <Td><AppBadge app={log.app} /></Td>
                    <Td align="end" className="text-caption text-faint" dir="ltr">{log.ipAddress ?? '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

      {inspect && (
        <Modal open onClose={() => setInspect(null)} title={inspect.action} size="lg">
          <dl className="mb-4 grid gap-2 sm:grid-cols-2">
            <div>
              <dt className="text-caption uppercase text-[var(--text-tertiary)]">{t('audit.occurredAt')}</dt>
              <dd className="text-body-sm tabular">{formatDateTime(inspect.occurredAt, locale)}</dd>
            </div>
            <div>
              <dt className="text-caption uppercase text-[var(--text-tertiary)]">{t('audit.actor')}</dt>
              <dd className="text-body-sm">{inspect.actor?.fullName ?? 'system'}</dd>
            </div>
            <div>
              <dt className="text-caption uppercase text-[var(--text-tertiary)]">{t('audit.entity')}</dt>
              <dd className="text-body-sm">
                {inspect.entityType} <span className="font-mono text-caption text-faint">{inspect.entityId}</span>
              </dd>
            </div>
            <div>
              <dt className="text-caption uppercase text-[var(--text-tertiary)]">User agent</dt>
              <dd className="truncate text-caption text-faint" dir="ltr">{inspect.userAgent ?? '—'}</dd>
            </div>
          </dl>

          {inspect.oldValues && (
            <JsonBlock title="Before" value={inspect.oldValues} tone="error" />
          )}
          {inspect.newValues && <JsonBlock title="After" value={inspect.newValues} tone="success" />}
          {inspect.metadata && <JsonBlock title="Metadata" value={inspect.metadata} tone="neutral" />}
        </Modal>
      )}
    </>
  );
}

/**
 * Which system the action was taken in. Rows from before sys existed, and from
 * background jobs, have none — a dash says so rather than guessing.
 */
function AppBadge({ app }: { app: 'ADMIN' | 'SYS' | null }) {
  const { t } = useI18n();
  if (!app) return <span className="text-faint">—</span>;
  return <Badge tone={app === 'SYS' ? 'brand' : 'neutral'}>{t(app === 'SYS' ? 'systems.sys' : 'systems.admin')}</Badge>;
}

function JsonBlock({ title, value, tone }: { title: string; value: unknown; tone: 'error' | 'success' | 'neutral' }) {
  const borders = {
    error: 'border-error/30 bg-error/6',
    success: 'border-success/30 bg-success/6',
    neutral: 'border-[var(--line)] bg-[var(--surface-sunken)]',
  };
  return (
    <div className={`mb-3 rounded-md border p-3 ${borders[tone]}`}>
      <p className="mb-1.5 text-caption uppercase text-[var(--text-tertiary)]">{title}</p>
      <pre className="overflow-x-auto font-mono text-mono-sm text-[var(--text-primary)]" dir="ltr">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
